import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import ApiKeyForm from './ApiKeyForm';
import ApiTierOrderModal from './ApiTierOrderModal';
import ApiTierSetModal from './ApiTierSetModal';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { canOrderPaidFeatures } from '../billing/functions/billingUtils';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';

// Rename the name of the function:
const ApiKeyList: React.FC = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentType, allowedRights } = useAuthContext();

    // Filter the list on the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionParams = !isEnvironmentSelected && selectedDivision ? { 'division_scope': selectedDivision.id } : {};

    // Specify the options of this list:
    const query = {
        endpoint: 'get_api_token_list',
        defaultParams: { ...divisionParams },
        object: 'api_token'
    }
    const objectName = 'api_key.general.object_name'
    const showSearch = false
    const showDeleteOrDeactivate = 'fully_delete'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <ApiKeyForm />;

    // Superusers and staff set the tier directly, without an order or payment
    const canSetTier = allowedRights.includes('no_user') || allowedRights.includes('staff_user');

    // Only environment types with a paid subscription can order api access
    const canUpgradeTier = canSetTier || canOrderPaidFeatures(environmentType);

    // Open the tier set modal for superusers, otherwise the api tier order modal
    const openTierOrderModal = (event: React.MouseEvent, tokenId: number, currentTier: string) => {
        event.stopPropagation();
        initializeModal(
            canSetTier
                ? <ApiTierSetModal tokenId={tokenId} currentTier={currentTier} />
                : <ApiTierOrderModal tokenId={tokenId} />,
            { modalSize: 'medium' }
        );
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { field: 'token', label: 'api_key.general.key_label', width: '2fr' },
        { field: 'name', label: '', width: '1fr', },
        {
            field: 'rate_limit_tier',
            label: 'api_key.general.tier_label',
            width: '2fr',
            render: (value, item) => (
                <span>
                    ({t(`api_key.tier.${value}`)} {t('api_key.general.level_suffix')})
                    {(canSetTier || value !== 'extended') && canUpgradeTier && (
                        // Show an upgrade link behind the level of free keys, superusers can set every level
                        <span
                            className='module-order-link upgrade-tier-link'
                            onClick={(event) => openTierOrderModal(event, item.id, value)}
                        >
                            {t(canSetTier ? 'api_key.general.set_tier_label' : 'api_key.general.upgrade_tier_label')}
                        </span>
                    )}
                </span>
            )
        },
    ];

    return (
        <AbstractList
            key={!isEnvironmentSelected && selectedDivision ? selectedDivision.id : 'environment'}
            query={query}
            objectName={objectName}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            isPaginated={isPaginated}
        />
    );
};

export default ApiKeyList;
